import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

type Session = { id: string; squadId: string; title: string; date: string; paymentMode: string; headcount: number; rateCents: number; reservedElsewhere: boolean; selected?: boolean };
type Pricing = Record<string, { rate: string; students: string }>;
const money = (cents: number) => (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function CustomInvoiceClasses({ invoiceId, squadIds, startDate, endDate, revision, disabled, onApply }: {
  invoiceId: string; squadIds: string[]; startDate: string; endDate: string; revision: number; disabled: boolean;
  onApply: (data: Record<string, unknown>) => Promise<void>;
}) {
  const [start, setStart] = useState(startDate);
  const [end, setEnd] = useState(endDate);
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [pricing, setPricing] = useState<Pricing>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reviewedKey, setReviewedKey] = useState('');
  const selectionKey = JSON.stringify([squadIds, start, end, revision]);
  const groups = Array.from(new Set((sessions || []).map(session => session.squadId)));
  const activeGroups = groups.filter(id => sessions?.some(session => session.squadId === id && selected.includes(session.id)));
  const valid = activeGroups.every(id => {
    const { rate, students } = pricing[id];
    return /^\d+(\.\d{1,2})?$/.test(rate) && /^\d+$/.test(students)
      && Number.isSafeInteger(Math.round(Number(rate) * 100)) && Number.isSafeInteger(Number(students));
  });
  const total = activeGroups.reduce((sum, id) => {
    const rows = sessions!.filter(session => session.squadId === id && selected.includes(session.id));
    return sum + Math.round(Number(pricing[id].rate) * 100) * rows.length * (rows[0].paymentMode === 'school_enrollment' ? Number(pricing[id].students) : 1);
  }, 0);
  const stale = reviewedKey !== selectionKey;
  const request = { invoiceId, squadIds, startDate: start, endDate: end, expectedRevision: revision, customSelection: true };
  async function review() {
    setBusy(true); setError(''); setSessions(null);
    try {
      const result: any = await httpsCallable(getFunctions(), 'previewCustomInvoiceClasses')({ ...request, preview: true });
      const rows: Session[] = result?.data?.sessions;
      if (!Array.isArray(rows) || rows.some(row => !row || typeof row.id !== 'string'
        || typeof row.squadId !== 'string' || typeof row.date !== 'string'
        || !Number.isSafeInteger(row.headcount) || row.headcount < 0
        || !Number.isSafeInteger(row.rateCents) || row.rateCents < 0)) {
        throw new Error('The billing service returned an incompatible class preview. Deploy the updated billing functions, then review classes again.');
      }
      setSessions(rows); setSelected(rows.filter(row => row.selected !== false).map(row => row.id));
      setPricing(Object.fromEntries(rows.map(row => [row.squadId, { rate: (row.rateCents / 100).toFixed(2), students: String(row.headcount) }])));
      setReviewedKey(selectionKey);
    } catch (e: any) {
      setError(['functions/not-found', 'functions/unimplemented'].includes(e.code)
        ? 'Class review is not available on this server yet. Deploy the updated billing functions, then try again.'
        : e.message || 'Could not load classes.');
    }
    finally { setBusy(false); }
  }
  async function apply() {
    setBusy(true); setError('');
    try {
      await onApply({ ...request, sessionIds: selected, overrides: activeGroups.map(squadId => ({ squadId, rateCents: Math.round(Number(pricing[squadId].rate) * 100), headcount: Number(pricing[squadId].students) })) });
      setSessions(null);
    } catch (e: any) { setError(e.message || 'Could not update invoice.'); }
    finally { setBusy(false); }
  }
  return <details className="custom-invoice-classes">
    <summary>Choose classes and pricing for this invoice</summary>
    <p>Select a date range, review the classes, and adjust pricing for this invoice only. This replaces its squad charges; separate manual charges stay on the invoice.</p>
    <fieldset disabled={disabled || busy}>
      <div className="billing-adhoc-range">
        <label>First class date<input type="date" value={start} onChange={event => setStart(event.target.value)} /></label>
        <label>Last class date<input type="date" value={end} min={start} onChange={event => setEnd(event.target.value)} /></label>
        <button type="button" className="btn btn-secondary" onClick={review} disabled={!squadIds.length || !start || !end || start > end}>{busy ? 'Working…' : 'Review classes'}</button>
      </div>
      {sessions && !stale && <>
        <p>Already sent classes are excluded. Classes reserved by another draft are marked; only unsent automatic drafts can transfer classes.</p>
        {groups.map(id => {
          const rows = sessions.filter(row => row.squadId === id);
          const enrollment = rows[0].paymentMode === 'school_enrollment';
          const count = rows.filter(row => selected.includes(row.id)).length;
          const subtotal = Math.round(Number(pricing[id].rate) * 100) * count * (enrollment ? Number(pricing[id].students) : 1);
          return <section className="custom-invoice-squad" key={id}>
            <h4>{rows[0].title}</h4>
            <div className="custom-invoice-dates">{rows.map(row => <label key={row.id}>
              <input type="checkbox" checked={selected.includes(row.id)} onChange={event => setSelected(current => event.target.checked ? [...current, row.id] : current.filter(key => key !== row.id))} />
              {new Date(`${row.date}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}{row.reservedElsewhere ? ' · Other draft' : ''}
            </label>)}</div>
            <div className="custom-invoice-pricing">
              <label>{enrollment ? 'Rate / student / class ($)' : 'Rate / class ($)'}<input type="number" min="0" step="0.01" value={pricing[id].rate} onChange={event => setPricing(current => ({ ...current, [id]: { ...current[id], rate: event.target.value } }))} /></label>
              {enrollment && <label>Students<input type="number" min="0" step="1" value={pricing[id].students} onChange={event => setPricing(current => ({ ...current, [id]: { ...current[id], students: event.target.value } }))} /></label>}
              <span>{count} {count === 1 ? 'class' : 'classes'}{enrollment ? ` × ${pricing[id].students || '0'} students` : ''} × {money(Math.round(Number(pricing[id].rate) * 100))} = <strong>{money(subtotal)}</strong></span>
            </div>
          </section>;
        })}
        {!sessions.length ? <p>No unbilled classes in this range. Choose another range or squad.</p> : <div className="custom-invoice-actions"><strong>Selected classes: {valid ? money(total) : 'Check pricing'}</strong><button type="button" className="btn btn-primary" disabled={!selected.length || !valid || !Number.isSafeInteger(total)} onClick={apply}>Apply to invoice</button></div>}
      </>}
      {sessions && stale && <p>Selection changed. Review classes again before applying.</p>}
    </fieldset>
    {error && <p role="alert" className="error">{error}</p>}
  </details>;
}
